import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  Card,
  Cifra,
  FilaCifras,
  Aviso,
  SERIES,
  OTROS,
  COLOR,
  eur,
  eur0,
  pct,
  num,
  mesCorto,
  ejes,
  leyenda,
  barra,
  segmento,
  linea,
  useEndpoint,
} from '../ui';
import { BarrasH } from '../PortfolioCards';

/**
 * Dinero: lo que factura ClaimUp por cada línea —extrajudicial y judicial con
 * los «Precios recobro» de cada empresa (el mismo cálculo que su pestaña de
 * Facturación), AR con su tabla de tramos—, lo cobrado, la fase y el peso de
 * cada cliente, y AR frente a recobro.
 */

/** En la pila caben 7 clientes y «Otros» (gris): la paleta categórica tiene 8 colores y no se cicla. */
const CLIENTES_EN_PILA = 7;

/** Las líneas que factura ClaimUp, con el campo de cada mes del que sale su honorario. */
export const LINEAS = [
  { id: 'extrajudicial', label: 'Recobro extrajudicial', campo: 'fee_extrajudicial' },
  { id: 'judicial', label: 'Judicial', campo: 'fee_judicial' },
  { id: 'ar', label: 'AR', campo: 'fee_ar' },
];

/** `?lineas=extrajudicial,ar` → las elegidas. Sin el parámetro, las tres. */
export const lineasDeUrl = valor => {
  const elegidas = (valor || '').split(',').filter(id => LINEAS.some(l => l.id === id));
  return elegidas.length ? elegidas : LINEAS.map(l => l.id);
};

const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);

export default function DineroSection({ billing, params }) {
  const fase = useEndpoint('/portfolio-performance/phase-comparison', params);
  const b = billing.data;
  const ok = billing.estado === 'ok' && b;

  const sinPrecios = ok ? b.without_pricing : [];

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra
          etiqueta='Facturado en recobro (sin IVA)'
          cargando={billing.estado === 'loading'}
          valor={ok ? eur0(b.totals.fee) : '—'}
          pie={
            ok && b.totals.fee_pct_of_collected != null
              ? `${pct(b.totals.fee_pct_of_collected)} de lo cobrado con precio`
              : null
          }
        />
        <Cifra
          etiqueta='Cobrado en recobro'
          cargando={billing.estado === 'loading'}
          valor={ok ? eur0(b.totals.collected) : '—'}
        />
        <Cifra
          etiqueta='Cobrado sin precio definido'
          cargando={billing.estado === 'loading'}
          valor={ok ? eur0(b.totals.collected_without_pricing) : '—'}
          tono={ok && b.totals.collected_without_pricing > 0 ? 'alerta' : 'ok'}
          pie={
            ok
              ? sinPrecios.length
                ? `${sinPrecios.length} cliente(s) sin «Precios recobro»`
                : 'Todos con precio'
              : null
          }
        />
        <Cifra
          etiqueta='Facturado en AR (tramos)'
          cargando={billing.estado === 'loading'}
          valor={ok ? eur0(b.totals.fee_ar) : '—'}
        />
        <Cifra
          etiqueta='Facturado en total'
          cargando={billing.estado === 'loading'}
          valor={ok ? eur0(b.totals.fee_total) : '—'}
          pie='Recobro y AR, sin IVA'
        />
      </FilaCifras>

      {ok && sinPrecios.length > 0 && (
        <Aviso>
          <strong>Facturación sin definir:</strong> estos clientes cobraron en el periodo y no tienen «Precios recobro»
          en su perfil, así que sus honorarios no se suman (nunca como 0 €):{' '}
          {sinPrecios.map((c, i) => (
            <span key={c.company_id}>
              {i > 0 && ', '}
              <a className='underline' href={`/profile/${c.company_id}`} target='_blank' rel='noreferrer'>
                {c.name}
              </a>{' '}
              ({eur0(c.collected)})
            </span>
          ))}
          .
        </Aviso>
      )}

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FacturacionPorCliente billing={billing} />
        <CobradoFrenteAHonorarios billing={billing} />
        <JudicialFrenteAExtrajudicial billing={billing} />
        <PesoDeCadaCliente billing={billing} />
        <ArFrenteARecobro billing={billing} fase={fase} />
      </div>
    </div>
  );
}

/** Los botones del filtro de líneas: se encienden y apagan; al menos una queda siempre. */
function FiltroLineas({ elegidas, onChange }) {
  const cambiar = id => {
    const nuevas = elegidas.includes(id) ? elegidas.filter(x => x !== id) : [...elegidas, id];
    if (nuevas.length) onChange(LINEAS.map(l => l.id).filter(x => nuevas.includes(x)));
  };
  return (
    <div className='flex flex-wrap items-center gap-1 mb-3' role='group' aria-label='Líneas de negocio'>
      <span className='text-xs text-slate-500 mr-1'>Líneas:</span>
      {LINEAS.map(l => {
        const activa = elegidas.includes(l.id);
        return (
          <button
            key={l.id}
            type='button'
            aria-pressed={activa}
            onClick={() => cambiar(l.id)}
            className={`text-xs px-2.5 py-1 rounded-lg border ${
              activa
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-slate-300 text-slate-500 hover:text-slate-800'
            }`}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function FacturacionPorCliente({ billing }) {
  const b = billing.data;
  const [sp, setSp] = useSearchParams();
  const elegidas = lineasDeUrl(sp.get('lineas'));
  const todas = elegidas.length === LINEAS.length;
  const setElegidas = nuevas => {
    const next = new URLSearchParams(sp);
    if (nuevas.length === LINEAS.length) next.delete('lineas');
    else next.set('lineas', nuevas.join(','));
    setSp(next, { replace: true });
  };

  const { chart, filas, columnas, total } = useMemo(() => {
    if (!b) return { chart: null, filas: [], columnas: [], total: 0 };
    const campos = LINEAS.filter(l => elegidas.includes(l.id)).map(l => l.campo);
    // Lo facturado por un cliente en un mes, en las líneas elegidas. null si
    // ninguna de ellas tiene precio para él (no es un cero).
    const valor = (c, m) => {
      const x = c.months[m];
      if (!x) return 0;
      const vals = campos.map(k => x[k]);
      return vals.every(v => v == null) ? null : sum(vals, v => v);
    };
    const totalDe = c => sum(b.months, m => valor(c, m));
    const conFacturacion = b.companies
      .map(c => ({ c, t: totalDe(c) }))
      .filter(x => x.t > 0)
      .sort((x, y) => y.t - x.t)
      .map(x => x.c);
    const top = conFacturacion.slice(0, CLIENTES_EN_PILA);
    const resto = conFacturacion.slice(CLIENTES_EN_PILA);
    const datasets = top.map((c, i) =>
      segmento(
        c.name,
        b.months.map(m => valor(c, m) || 0),
        SERIES[i]
      )
    );
    if (resto.length) {
      datasets.push(
        segmento(
          `Otros (${resto.length})`,
          b.months.map(m => sum(resto, c => valor(c, m))),
          OTROS
        )
      );
    }
    const cols = [
      { key: 'name', label: 'Cliente' },
      ...b.months.map(m => ({ key: m, label: mesCorto(m), fmt: eur })),
      { key: 'total', label: 'Total', fmt: eur },
    ];
    const rows = b.companies
      .map(c => {
        const meses = Object.fromEntries(b.months.map(m => [m, c.months[m] ? valor(c, m) : null]));
        const sinPrecio = campos.some(k => k !== 'fee_ar') && c.has_recovery && !c.has_pricing;
        return { name: sinPrecio ? `${c.name} (sin precios de recobro)` : c.name, ...meses, total: totalDe(c) };
      })
      .filter(r => r.total > 0 || r.name.endsWith('(sin precios de recobro)'))
      .sort((x, y) => y.total - x.total);
    return {
      chart: { labels: b.months.map(mesCorto), datasets },
      filas: rows,
      columnas: cols,
      total: sum(rows, r => r.total),
    };
  }, [b, elegidas.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const nombres = LINEAS.filter(l => elegidas.includes(l.id)).map(l => l.label.toLowerCase());
  return (
    <Card
      titulo='¿Cuánto facturamos cada mes, y a quién?'
      lectura={`Honorarios por mes, apilados por cliente: la altura de la barra es lo facturado ese mes${
        todas ? '' : ` en ${nombres.join(' y ')}`
      }.`}
      nota='Recobro con los «Precios recobro» de cada empresa y el mismo cálculo que su pestaña de Facturación (escalado por días, tope mensual y demandas presentadas, que van en judicial). Si el tope recorta, se reparte en proporción entre extrajudicial y judicial. AR con su tabla de tramos. Sin IVA ni tasas judiciales.'
      estado={billing.estado}
      vacio={!b || !total}
      vacioTexto={
        todas
          ? 'No hay honorarios en este periodo: o no hubo cobros, o los clientes que cobraron no tienen precios.'
          : `No hay facturación en ${nombres.join(' ni ')} en este periodo.`
      }
      filas={filas}
      columnas={columnas}
      csv={`facturacion-por-cliente${todas ? '' : `-${elegidas.join('-')}`}`}
      ancho
      alto={320}
      encabezado={<FiltroLineas elegidas={elegidas} onChange={setElegidas} />}
    >
      {chart && (
        <Chart
          type='bar'
          data={chart}
          options={{
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: leyenda,
              tooltip: {
                callbacks: {
                  label: ctx => (ctx.parsed.y ? `${ctx.dataset.label}: ${eur(ctx.parsed.y)}` : null),
                  footer: items => `Total: ${eur(items.reduce((s, i) => s + i.parsed.y, 0))}`,
                },
              },
            },
            scales: {
              ...ejes('€'),
              x: { stacked: true, grid: { display: false } },
              y: { ...ejes('€').y, stacked: true },
            },
          }}
        />
      )}
    </Card>
  );
}

function CobradoFrenteAHonorarios({ billing }) {
  const b = billing.data;
  const filas = (b?.by_month || []).map(m => {
    const cobrado = m.collected_extrajudicial + m.collected_judicial;
    const conPrecio = cobrado - m.collected_without_pricing;
    return {
      mes: m.month,
      cobrado_con_precio: Math.round(conPrecio * 100) / 100,
      cobrado_sin_precio: m.collected_without_pricing,
      honorarios: m.fee,
      pct: conPrecio > 0 ? Math.round((m.fee / conPrecio) * 10000) / 100 : null,
    };
  });
  return (
    <Card
      titulo='¿Cuánto de lo cobrado se queda como honorario?'
      lectura='Barras: lo cobrado cada mes. Línea: lo que facturamos sobre ello. Misma escala, en euros.'
      nota='El cobrado «sin precio» va aparte y en gris: es de clientes sin «Precios recobro», y no se le calcula honorario.'
      estado={billing.estado}
      vacio={!filas.some(f => f.cobrado_con_precio || f.cobrado_sin_precio)}
      filas={filas}
      csv='cobrado-frente-a-honorarios'
      columnas={[
        { key: 'mes', label: 'Mes', fmt: mesCorto },
        { key: 'cobrado_con_precio', label: 'Cobrado con precio', fmt: eur },
        { key: 'cobrado_sin_precio', label: 'Cobrado sin precio', fmt: eur },
        { key: 'honorarios', label: 'Honorarios', fmt: eur },
        { key: 'pct', label: '% honorario', fmt: pct },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: filas.map(f => mesCorto(f.mes)),
          datasets: [
            // Su propia pila: en un eje apilado, sin ella la línea se sumaría encima de las barras.
            linea(
              'Honorarios',
              filas.map(f => f.honorarios),
              COLOR.s2,
              { stack: 'honorarios' }
            ),
            segmento(
              'Cobrado con precio',
              filas.map(f => f.cobrado_con_precio),
              COLOR.s1
            ),
            segmento(
              'Cobrado sin precio',
              filas.map(f => f.cobrado_sin_precio),
              OTROS
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: leyenda,
            tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${eur(ctx.parsed.y)}` } },
          },
          scales: {
            ...ejes('€'),
            x: { stacked: true, grid: { display: false } },
            y: { ...ejes('€').y, stacked: true },
          },
        }}
      />
    </Card>
  );
}

function JudicialFrenteAExtrajudicial({ billing }) {
  const filas = (billing.data?.by_month || []).map(m => ({
    mes: m.month,
    extrajudicial: m.collected_extrajudicial,
    judicial: m.collected_judicial,
    honorarios_extrajudicial: m.fee_extrajudicial,
    honorarios_judicial: m.fee_judicial,
    demandas: m.fee_demands,
  }));
  return (
    <Card
      titulo='¿Cuánto entra por vía judicial y cuánto por extrajudicial?'
      lectura='Lo cobrado cada mes por fase, apilado. La tabla trae además los honorarios de cada fase y el precio de las demandas.'
      nota='Fase = tipo de pago: extrajudicial o consignación judicial. Si el tope mensual recorta, los honorarios se reparten entre las dos fases en proporción; el precio de las demandas va en judicial.'
      estado={billing.estado}
      vacio={!filas.some(f => f.extrajudicial || f.judicial)}
      filas={filas}
      csv='judicial-frente-a-extrajudicial'
      columnas={[
        { key: 'mes', label: 'Mes', fmt: mesCorto },
        { key: 'extrajudicial', label: 'Cobrado extrajudicial', fmt: eur },
        { key: 'judicial', label: 'Cobrado judicial', fmt: eur },
        { key: 'honorarios_extrajudicial', label: 'Honorarios extrajudicial', fmt: eur },
        { key: 'honorarios_judicial', label: 'Honorarios judicial', fmt: eur },
        { key: 'demandas', label: '… de ellos, demandas presentadas', fmt: eur },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: filas.map(f => mesCorto(f.mes)),
          datasets: [
            segmento(
              'Extrajudicial',
              filas.map(f => f.extrajudicial),
              COLOR.s1
            ),
            segmento(
              'Judicial',
              filas.map(f => f.judicial),
              COLOR.s2
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: leyenda,
            tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${eur(ctx.parsed.y)}` } },
          },
          scales: {
            ...ejes('€'),
            x: { stacked: true, grid: { display: false } },
            y: { ...ejes('€').y, stacked: true },
          },
        }}
      />
    </Card>
  );
}

const CLIENTES_EN_PESO = 12;

function PesoDeCadaCliente({ billing }) {
  const filas = (billing.data?.companies || []).map(c => ({
    name: c.name,
    fee: c.fee,
    fee_ar: c.fee_ar,
    fee_total: c.fee_total,
    collected: c.collected,
    share_pct: c.share_pct,
    precios: !c.has_recovery ? '—' : c.has_pricing ? 'Sí' : 'No',
  }));
  const top = filas.filter(f => f.share_pct != null && f.share_pct > 0).slice(0, CLIENTES_EN_PESO);
  return (
    <Card
      titulo='¿Qué peso tiene cada cliente en lo facturado?'
      lectura='El % de los honorarios del periodo que viene de cada cliente. Si uno solo pesa mucho, la facturación depende de él.'
      nota={`El gráfico enseña los ${CLIENTES_EN_PESO} que más pesan; la tabla, todos, también los que no tienen precios.`}
      estado={billing.estado}
      vacio={!top.length}
      filas={filas}
      csv='peso-de-cada-cliente'
      columnas={[
        { key: 'name', label: 'Cliente' },
        { key: 'fee', label: 'Recobro', fmt: eur },
        { key: 'fee_ar', label: 'AR', fmt: eur },
        { key: 'fee_total', label: 'Total facturado', fmt: eur },
        { key: 'collected', label: 'Cobrado en recobro', fmt: eur },
        { key: 'share_pct', label: 'Peso', fmt: pct },
        { key: 'precios', label: 'Precios recobro' },
      ]}
      alto={Math.max(220, top.length * 26)}
    >
      <BarrasH titulo='Peso' etiquetas={top.map(f => f.name)} valores={top.map(f => f.share_pct)} fmt={pct} />
    </Card>
  );
}

function ArFrenteARecobro({ billing, fase }) {
  const filas = useMemo(() => {
    if (fase.estado !== 'ok' || billing.estado !== 'ok') return [];
    const ph = fase.data.phases;
    const de = f => ph.find(p => p.fase === f) || { expedientes: 0, vivos: 0, deuda_asignada: 0 };
    const ar = de('account-receivable');
    const rec = ['extra-judicial', 'judicial'].map(de);
    const recExp = sum(rec, p => p.expedientes);
    const recVivos = sum(rec, p => p.vivos);
    const recDeuda = sum(rec, p => p.deuda_asignada);
    const conPrecio = billing.data.totals.collected - billing.data.totals.collected_without_pricing;
    // El ingreso de AR viene en la facturación (sus tramos), mes a mes.
    const arB = billing.data.ar;
    const mesesAr = billing.data.months.length - (arB.months_failed || []).length;
    return [
      {
        fase: 'Recobro (extrajudicial y judicial)',
        expedientes: recExp,
        vivos: recVivos,
        deuda_media: recExp ? recDeuda / recExp : null,
        ingreso: billing.data.totals.fee,
        ingreso_por_expediente: recVivos ? billing.data.totals.fee / recVivos : null,
        honorario_medio: conPrecio > 0 ? (billing.data.totals.fee / conPrecio) * 100 : null,
        base: 'sobre lo cobrado',
      },
      {
        fase: 'AR',
        expedientes: ar.expedientes,
        vivos: ar.vivos,
        deuda_media: ar.expedientes ? ar.deuda_asignada / ar.expedientes : null,
        ingreso: arB.revenue,
        ingreso_por_expediente: ar.vivos ? arB.revenue / ar.vivos : null,
        honorario_medio: arB.managed_avg && mesesAr ? (arB.revenue / mesesAr / arB.managed_avg) * 100 : null,
        base: 'sobre lo gestionado al mes',
      },
    ];
  }, [fase, billing]);
  const estado =
    fase.estado === 'error' || billing.estado === 'error'
      ? 'error'
      : fase.estado === 'ok' && billing.estado === 'ok'
        ? 'ok'
        : 'loading';
  return (
    <Card
      titulo='¿Rinde más un deudor de AR o uno de recobro?'
      lectura='Ingreso del periodo por expediente vivo en cada línea de negocio. Un expediente es un deudor de un cliente.'
      nota='Ingreso: el periodo elegido, recobro con sus «Precios recobro» y AR con su tabla de tramos, repartido entre los expedientes en gestión viva hoy. Deuda media: toda la historia. El honorario medio de AR es sobre los euros gestionados al mes, no sobre lo cobrado.'
      estado={estado}
      vacio={!filas.length || !filas.some(f => f.expedientes)}
      filas={filas}
      csv='ar-frente-a-recobro'
      columnas={[
        { key: 'fase', label: 'Línea' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'vivos', label: 'En gestión viva', fmt: num },
        { key: 'deuda_media', label: 'Deuda media', fmt: eur },
        { key: 'ingreso', label: 'Ingreso del periodo', fmt: eur },
        { key: 'ingreso_por_expediente', label: 'Ingreso por expediente vivo', fmt: eur },
        { key: 'honorario_medio', label: 'Honorario medio', fmt: pct },
        { key: 'base', label: 'Base del honorario' },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: filas.map(f => f.fase),
          datasets: [
            barra(
              'Ingreso por expediente',
              filas.map(f => f.ingreso_por_expediente),
              COLOR.s1
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `Ingreso por expediente: ${eur(ctx.parsed.y)}` } },
          },
          scales: ejes('€ por expediente'),
        }}
      />
    </Card>
  );
}
